'use client';

import clsx from 'clsx';
import { ArrowLeft, History, SearchIcon, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { AppLink } from '@//components/AppLink';
import { Button } from '../Button';
import { Spinner } from '../Spinner';
import styles from './InputSearchAdvanced.module.scss';

type InputSearchAdvancedAllowedChildren =
  | React.ReactElement<React.ComponentProps<typeof InputSearchAdvancedSuggestion>>
  | boolean;

interface InputSearchAdvancedActionProps {
  label: string;
  title: string;
  onClick: () => void;
}

export type InputSearchAdvancedVariant = 'default' | 'on-fill';
export type InputSearchAdvancedScale = 'default' | 'lg';

interface InputSearchAdvancedProps extends React.HTMLProps<HTMLInputElement> {
  id: string;
  label: string;
  children?:
    | InputSearchAdvancedAllowedChildren
    | InputSearchAdvancedAllowedChildren[]
    | (InputSearchAdvancedAllowedChildren | InputSearchAdvancedAllowedChildren[])[];
  register?: UseFormRegisterReturn;
  suggestionExpanded?: boolean; // Set to true when the suggestion list is expanded
  suggestionListId?: string; // ID of the suggestion list
  suggestionActiveOptionId?: string; // ID of the active suggestion option
  variant?: InputSearchAdvancedVariant;
  scale?: InputSearchAdvancedScale;
  loading?: boolean;
  testId?: string;
  button?: InputSearchAdvancedActionProps;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClose?: () => void; // Callback when the search should be closed
}

export const InputSearchAdvanced: React.FC<InputSearchAdvancedProps> = ({
  id,
  label,
  children,
  register,
  suggestionExpanded,
  suggestionListId,
  suggestionActiveOptionId,
  variant = 'default',
  scale = 'default',
  loading,
  testId = id,
  button,
  className,
  onChange,
  onKeyDown,
  onClose,
  ref,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Only apply combobox ARIA attributes when suggestions are available
  const hasComboboxBehavior = suggestionListId && suggestionExpanded !== undefined;

  // Component is considered "open" when input is focused OR suggestions are expanded
  const isOpen = isFocused || suggestionExpanded;

  // Handle focus events - compose with props handlers
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Call props onFocus if it exists
      props.onFocus?.(e);
    },
    [props],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Delay blur to check if focus moved within the component
      setTimeout(() => {
        if (!comboboxRef.current?.contains(document.activeElement)) {
          setIsFocused(false);
          onClose?.();
        }
      }, 0);
      // Call register's onBlur if it exists (react-hook-form uses it)
      register?.onBlur?.(e);
      // Call props onBlur if it exists
      props.onBlur?.(e);
    },
    [props, onClose, register],
  );

  // Handle ESC and Enter keys to close
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setIsFocused(false);
        inputRef.current?.blur();
        onClose?.();
      } else if (e.key === 'Enter') {
        // Explicitly clear focus state when Enter is pressed
        // This ensures the backdrop closes properly when navigating via keyboard
        setIsFocused(false);
      }
      // Call props onKeyDown if it exists
      onKeyDown?.(e);
    },
    [onKeyDown, onClose],
  );

  // Handle onChange - compose with register and props handlers
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Call register's onChange if it exists (react-hook-form uses it)
      register?.onChange?.(e);
      // Call props onChange if it exists
      onChange?.(e);
    },
    [onChange, register],
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target as Node)) {
        setIsFocused(false);
        onClose?.();
      }
    };

    if (isOpen) {
      // Use click instead of mousedown to allow option onClick handlers to execute first
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen, onClose]);

  return (
    <div
      role={hasComboboxBehavior ? 'combobox' : undefined}
      aria-expanded={hasComboboxBehavior ? suggestionExpanded : undefined}
      aria-controls={hasComboboxBehavior ? suggestionListId : undefined}
      aria-owns={hasComboboxBehavior ? suggestionListId : undefined}
      aria-haspopup={hasComboboxBehavior ? 'listbox' : undefined}
      className={clsx(styles.combobox, className, {
        [styles.variantOnFill]: variant === 'on-fill',
        [styles.sizeLarge]: scale === 'lg',
        [styles.comboboxOpen]: isOpen,
        'lock-body-lt-sm': isOpen,
      })}
      ref={comboboxRef}
    >
      <header className={styles.header}>
        <button
          type="button"
          onClick={() => {
            setIsFocused(false);
            onClose?.();
          }}
          className={styles.closeButton}
        >
          <span className="sr-only">Close search</span>
          <ArrowLeft aria-hidden="true" />
        </button>
        <div className={styles.inputWrapper}>
          <label htmlFor={id} className={styles.iconLabel}>
            <SearchIcon aria-hidden="true" />
            <span className="sr-only">{label}</span>
          </label>
          <input
            {...props}
            type="search"
            aria-autocomplete={hasComboboxBehavior ? 'list' : undefined}
            aria-activedescendant={
              hasComboboxBehavior && suggestionActiveOptionId ? suggestionActiveOptionId : undefined
            }
            id={id}
            aria-label={label}
            className={styles.input}
            ref={(el) => {
              // Set both refs
              inputRef.current = el;
              // Set register ref if it exists
              if (register?.ref) {
                register.ref(el);
              }
            }}
            // Spread register fields except event handlers and ref
            name={register?.name}
            required={register?.required}
            min={register?.min}
            max={register?.max}
            maxLength={register?.maxLength}
            minLength={register?.minLength}
            pattern={register?.pattern}
            disabled={register?.disabled}
            // Our handlers come last to ensure they're called
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            data-testid={testId}
            enterKeyHint="search"
          />
          {loading && (
            <span className={styles.loader}>
              <Spinner size="text" />
            </span>
          )}
          {button && (
            <Button
              size="md"
              variant="primary"
              onClick={button.onClick}
              title={button.title}
              className={styles.button}
            >
              {button.label}
            </Button>
          )}
        </div>
      </header>
      {children}
    </div>
  );
};

// Suggestion List

type SuggestionListAllowedChildren =
  | React.ReactElement<typeof InputSearchAdvancedSuggestionGroup>
  | boolean;

interface InputSearchAdvancedSuggestionProps {
  children?: SuggestionListAllowedChildren | SuggestionListAllowedChildren[];
  id: string;
  testId?: string;
}

export const InputSearchAdvancedSuggestion: React.FC<InputSearchAdvancedSuggestionProps> = ({
  children,
  id,
  testId = id,
}) => (
  <ul id={id} className={styles.list} data-testid={testId}>
    {children}
  </ul>
);

// Suggestion Group

export type SuggestionGroupAllowedChildren = React.ReactElement<
  typeof InputSearchAdvancedSuggestionOption
> | null;

export interface InputSearchAdvancedSuggestionGroupProps {
  id: string;
  label: string;
  children: SuggestionGroupAllowedChildren | SuggestionGroupAllowedChildren[];
}

export const InputSearchAdvancedSuggestionGroup: React.FC<
  InputSearchAdvancedSuggestionGroupProps
> = ({ id, label, children }) => (
  <li aria-labelledby={`${id}-label`} className={styles.group}>
    <div id={`${id}-label`} className={styles.groupLabel}>
      {label}
    </div>
    {children}
  </li>
);

// Suggestion Option

interface InputSearchAdvancedSuggestionOptionProps extends React.HTMLProps<HTMLAnchorElement> {
  id: string;
  href: string;
  title?: string;
  count?: number;
  isActive?: boolean;
  children: React.ReactNode;
  testId?: string;
  onMouseEnter?: () => void;
}

export const InputSearchAdvancedSuggestionOption: React.FC<
  InputSearchAdvancedSuggestionOptionProps
  // title is intentionally destructured and dropped so it is not spread onto
  // the Link — the suggestion label is already the visible accessible name.
> = ({
  id,
  href,
  title: _title,
  count,
  isActive,
  children,
  testId = id,
  onMouseEnter,
  ...props
}) => (
  <AppLink
    href={href}
    id={id}
    className={clsx(styles.option, { [styles.optionActive]: isActive })}
    data-testid={testId}
    tabIndex={-1}
    onMouseEnter={onMouseEnter}
    prefetch="viewport"
    {...props}
  >
    <span className={styles.optionLabel}>{children}</span>
    {count && (
      <>
        <span className="sr-only">({count} items)</span>
        <span className={styles.optionCount} aria-hidden="true">
          {count}
        </span>
      </>
    )}
  </AppLink>
);

// Previous Searches Option
// Used to display a previous search in the suggestion list

interface InputSearchAdvancedSearchHistoryOptionProps extends React.HTMLProps<HTMLAnchorElement> {
  href: string;
  label: string;
  title?: string;
  testId?: string;
  isActive?: boolean;
  onRemove?: () => void;
  onMouseEnter?: () => void;
}

export const InputSearchAdvancedSearchHistoryOption: React.FC<
  InputSearchAdvancedSearchHistoryOptionProps
  // title is intentionally destructured and dropped so it is not spread onto
  // the Link — the history label is already the visible accessible name.
> = ({ href, title: _title, label, testId, isActive, onRemove, onMouseEnter, ...props }) => {
  const t = useTranslations('components.inputSearchAdvanced');
  const tCommon = useTranslations('other.common');

  return (
    <div className={styles.searchHistoryOption} aria-label={label} onMouseEnter={onMouseEnter}>
      <AppLink
        href={href}
        className={clsx(styles.searchHistoryOptionLink, {
          [styles.searchHistoryOptionLinkActive]: isActive,
        })}
        data-testid={testId}
        tabIndex={-1}
        prefetch="viewport"
        {...props}
      >
        <History aria-hidden="true" />
        {label || tCommon('noFilters')}
      </AppLink>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className={styles.searchHistoryOptionRemoveButton}
          tabIndex={-1}
        >
          <span className="sr-only">{t('remove')}</span>
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
